/**
 * NotificationFeed — scrollable list of notifications displayed inside the
 * notification bell popover. Handles loading, error, and empty states.
 *
 * Requirements: 4.3, 4.6, 4.7, 4.8, 4.9, 4.10
 */

import React from 'react';
import { useNotificationContext } from '../context/NotificationContext';
import { NotificationItem } from './NotificationItem';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface NotificationFeedProps {
  /** Callback to close the parent popover */
  readonly onClose?: () => void;
}

export function NotificationFeed({ onClose }: NotificationFeedProps): React.ReactElement {
  const { notifications, loading, error, fetchNotifications, markRead, markAllRead } =
    useNotificationContext();

  // Fetch notifications when the feed is rendered (popover opened)
  React.useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <section
      aria-label="Notification feed"
      className="w-80 max-w-[calc(100vw-2rem)] bg-popover border rounded-md shadow-lg"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <h3 className="text-sm font-medium text-foreground">Notifications</h3>
        <button
          type="button"
          onClick={() => markAllRead()}
          className="text-xs text-primary hover:text-primary/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm px-1"
          aria-label="Mark all as read"
        >
          Mark all read
        </button>
      </div>

      {/* Content area */}
      <div className="max-h-96 overflow-y-auto" role="list" aria-label="Notifications list">
        {loading && (
          <div className="flex items-center justify-center py-8" role="status" aria-label="Loading notifications">
            <svg
              className="animate-spin h-5 w-5 text-muted-foreground"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="sr-only">Loading notifications</span>
          </div>
        )}

        {error && !loading && (
          <div className="px-3 py-6 text-center" role="alert">
            <p className="text-sm text-destructive mb-2">Could not load notifications.</p>
            <button
              type="button"
              onClick={() => fetchNotifications()}
              className="text-xs text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm px-1"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && notifications.length === 0 && (
          <div className="px-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">No notifications yet.</p>
          </div>
        )}

        {!loading && !error && notifications.length > 0 && (
          <ul className="py-1">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.notificationId}
                notification={notification}
                onMarkRead={markRead}
                onClose={onClose}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
